// PdfViewPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

interface PrivacyProps {
  pdfId?: string;
}

interface RestaurantInfo {
  name?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
}

interface EsercenteData {
  titolare_block: string;
}

const API_BASE =
  (process.env.REACT_APP_API_URL ||
    process.env.VITE_API_URL ||
    'http://localhost:5000/api');

const Privacy: React.FC<PrivacyProps> = ({ pdfId: propPdfId }) => {
  const { slug } = useParams<{ slug?: string }>();
  const [esercente, setEsercente] = useState<EsercenteData>({
    titolare_block: ""
  });

  useEffect(() => {
    if (!slug) {
      // No slug — leave block empty (dash placeholder will show)
      setEsercente({ titolare_block: "" });
      return;
    }

    // Fetch restaurant info via the public menu endpoint
    fetch(`${API_BASE}/menu/public/${slug}?lang=it`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data?.restaurant) {
          const r: RestaurantInfo = data.data.restaurant;
          const parts: string[] = [];

          if (r.name) parts.push(`<strong>${r.name}</strong>`);

          const addressLine = [r.address, r.postalCode, r.city]
            .filter(Boolean)
            .join(' – ');
          if (addressLine) parts.push(addressLine);

          if (r.phone) parts.push(`Tel: ${r.phone}`);
          if (r.email) parts.push(`E-mail: <a href="mailto:${r.email}" style="color:#b5410c">${r.email}</a>`);

          setEsercente({ titolare_block: parts.join('<br>') });
        } else {
          setEsercente({ titolare_block: "" });
        }
      })
      .catch(() => {
        setEsercente({ titolare_block: "" });
      });
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#f9f5ee] font-['Lora',Georgia,serif] text-[#1a1510] text-[15px] leading-[1.8]">
      {/* Header */}
      <header className="bg-[#1a1510] text-[#f9f5ee] py-14 px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent,transparent_48px,rgba(255,255,255,0.03)_48px,rgba(255,255,255,0.03)_50px)]" />
        <div className="relative z-10">
          <p className="font-['Lora',serif] text-[11px] tracking-[0.2em] uppercase text-[#e8c4aa] mb-4">
            Informativa ai sensi dell'art. 13 GDPR · Versione 1.1 · Aprile 2025
          </p>
          <h1 className="font-['Playfair_Display',Georgia,serif] text-[clamp(1.6rem,4vw,2.6rem)] font-bold leading-[1.2] mb-3">
            Informativa sul trattamento<br />dei dati personali
          </h1>
          <p className="text-[13px] text-[#b0a898] italic">powered by Km Zero Web Marketing Srls</p>
        </div>
      </header>

      {/* Scope Banner */}
      <div className="max-w-[780px] mx-auto mt-9 px-6">
        <div className="bg-[#1a1510] text-[#e8c4aa] rounded px-6 py-4 text-[12.5px] tracking-[0.04em] leading-[1.7] text-center">
          <strong className="text-[#f9f5ee]">Ambito di applicazione:</strong> la presente informativa si applica esclusivamente alle pagine del menù digitale accessibili tramite scansione del Codice QR presente presso il locale. Non si applica al sito web dell'Esercente né ad altri servizi da esso offerti, che dispongono di proprie informative privacy.
        </div>
      </div>

      {/* Table of Contents */}
      <div className="max-w-[780px] mx-auto mt-7 px-6">
        <div className="bg-[#f2ece0] border border-[#d6c9b0] rounded p-6">
          <p className="font-['Playfair_Display',serif] text-base font-semibold text-[#3d3528] mb-3.5 pb-2.5 border-b border-[#d6c9b0]">
            Indice
          </p>
          <ol className="pl-5 space-y-1">
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s1" className="text-[#b5410c] no-underline hover:underline">Titolare del trattamento e Responsabile del trattamento</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s2" className="text-[#b5410c] no-underline hover:underline">Dati trattati, finalità e basi giuridiche</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s3" className="text-[#b5410c] no-underline hover:underline">Trasferimento dei dati</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s4" className="text-[#b5410c] no-underline hover:underline">Conservazione dei dati</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s5" className="text-[#b5410c] no-underline hover:underline">Comunicazione dei dati a terzi</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s6" className="text-[#b5410c] no-underline hover:underline">Diritti dell'interessato</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s7" className="text-[#b5410c] no-underline hover:underline">Diritto di reclamo al Garante</a>
            </li>
            <li className="text-[13.5px] text-[#7a6f5e]">
              <a href="#s8" className="text-[#b5410c] no-underline hover:underline">Modifiche alla presente informativa</a>
            </li>
          </ol>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[780px] mx-auto my-11 px-6 pb-20">
        {/* Section Band */}
        <div className="bg-[#1a1510] text-[#f9f5ee] font-['Playfair_Display',serif] text-[0.7rem] tracking-[0.18em] uppercase px-3.5 py-1.5 inline-block mb-7 mt-13">
          Soggetti e ruoli
        </div>

        {/* Section 1 */}
        <h2 id="s1" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          1. Titolare del trattamento e Responsabile del trattamento
          <span className="inline-block bg-[#b5410c] text-white text-[10px] tracking-[0.12em] uppercase px-2 py-0.5 rounded align-middle ml-2">GDPR</span>
        </h2>

        <p className="mb-3.5 text-[#3d3528]">
          Ai sensi dell'art. 13 del Regolamento UE 2016/679 (<strong>"GDPR"</strong>), il soggetto di seguito indicato come <strong>Titolare del trattamento</strong> informa l'Utente circa le modalità di trattamento dei dati personali raccolti durante la navigazione delle pagine del menù digitale accessibili tramite Codice QR.
        </p>

        <div className="bg-[#fdfbf7] border-l-3 border-[#b5410c] p-4 my-5 rounded-r">
          <p className="text-[13.5px] mb-0">
            <strong>Titolare del trattamento</strong>
          </p>
          <div 
            id="titolare-block"
            className="text-[13.5px] text-[#3d3528] leading-[1.8] mt-2"
            dangerouslySetInnerHTML={{ 
              __html: esercente.titolare_block || '<span class="text-[#999] italic">—</span>' 
            }}
          />
        </div>

        <p className="mb-3.5 text-[#3d3528]">
          <strong>1.1 Responsabile del trattamento.</strong> Il Titolare ha designato quale Responsabile del trattamento, ai sensi dell'art. 28 GDPR, <strong>Km Zero Web Marketing Srls</strong>, con sede legale in Via Giuseppe Fontecedro 2, 01100 Viterbo (VT), P.IVA 02312590561, nella sua qualità di fornitore dell'infrastruttura tecnologica della piattaforma Menu Digitale. Km Zero tratta i dati esclusivamente su istruzione del Titolare del trattamento e non per finalità proprie.
        </p>

        <p className="mb-3.5 text-[#3d3528]">
          <strong>1.2 Natura del servizio.</strong> Le pagine del menù digitale sono pagine di sola consultazione. L'Utente non è tenuto a registrarsi, creare un account o fornire alcun dato personale per accedere al menù. Non sono presenti moduli di contatto, sistemi di prenotazione né funzionalità che richiedano l'inserimento di informazioni personali. I soli dati trattati sono quelli tecnici di navigazione descritti all'Articolo 2. Non vengono utilizzati cookie di profilazione né strumenti di tracciamento analitico di terze parti.
        </p>

        {/* Section Band */}
        <div className="bg-[#1a1510] text-[#f9f5ee] font-['Playfair_Display',serif] text-[0.7rem] tracking-[0.18em] uppercase px-3.5 py-1.5 inline-block mb-7 mt-13">
          Trattamento dei dati
        </div>

        {/* Section 2 */}
        <h2 id="s2" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          2. Dati trattati, finalità e basi giuridiche
        </h2>

        <p className="mb-3.5 text-[#3d3528]">
          Le pagine del menù digitale non raccolgono dati personali identificativi. Durante la navigazione vengono rilevati automaticamente dai server che ospitano la Piattaforma i seguenti dati tecnici di natura anonima, necessari al funzionamento del servizio:
        </p>

        {/* Table */}
        <div className="overflow-x-auto my-5">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="bg-[#1a1510] text-[#f9f5ee] px-3.5 py-2.5 text-left font-['Playfair_Display',serif] font-semibold text-[12px] tracking-[0.05em]">
                  Categoria di dati
                </th>
                <th className="bg-[#1a1510] text-[#f9f5ee] px-3.5 py-2.5 text-left font-['Playfair_Display',serif] font-semibold text-[12px] tracking-[0.05em]">
                  Finalità
                </th>
                <th className="bg-[#1a1510] text-[#f9f5ee] px-3.5 py-2.5 text-left font-['Playfair_Display',serif] font-semibold text-[12px] tracking-[0.05em]">
                  Base giuridica
                </th>
                <th className="bg-[#1a1510] text-[#f9f5ee] px-3.5 py-2.5 text-left font-['Playfair_Display',serif] font-semibold text-[12px] tracking-[0.05em]">
                  Conservazione
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="even:bg-[#fdfbf7]">
                <td className="px-3.5 py-2.5 border-b border-[#d6c9b0] align-top text-[#3d3528] leading-[1.6]">
                  <strong>Dati di navigazione tecnici</strong><br />
                  Indirizzo IP (registrato nei log di server in forma non associata all'identità dell'utente), tipo di dispositivo, sistema operativo, browser, pagine visitate, durata della sessione, data e ora dell'accesso.
                </td>
                <td className="px-3.5 py-2.5 border-b border-[#d6c9b0] align-top text-[#3d3528] leading-[1.6]">
                  Garantire il corretto funzionamento tecnico della Piattaforma; sicurezza informatica; prevenzione di abusi e accessi non autorizzati.
                </td>
                <td className="px-3.5 py-2.5 border-b border-[#d6c9b0] align-top text-[#3d3528] leading-[1.6]">
                  Legittimo interesse del Titolare del trattamento (art. 6.1.f GDPR)
                </td>
                <td className="px-3.5 py-2.5 border-b border-[#d6c9b0] align-top text-[#3d3528] leading-[1.6]">
                  Max 12 mesi, poi eliminazione automatica
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-[#f2ece0] border-l-3 border-[#7a6f5e] p-4 my-5 rounded-r text-[13px]">
          <p className="mb-0 text-[#7a6f5e] italic">
            Nessun dato personale identificativo (nome, cognome, indirizzo e-mail, numero di telefono o simili) viene raccolto tramite le pagine del menù digitale. Non vengono utilizzati cookie di profilazione, strumenti di tracciamento analitico di terze parti né sistemi pubblicitari. L'Utente accede al menù in forma completamente anonima.
          </p>
        </div>

        {/* Section 3 */}
        <h2 id="s3" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          3. Trasferimento dei dati
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          I dati tecnici di navigazione sono conservati esclusivamente su infrastrutture server localizzate all'interno dell'Unione Europea, di proprietà di Km Zero Web Marketing Srls. Non viene effettuato alcun trasferimento di dati verso Paesi terzi al di fuori dello Spazio Economico Europeo.
        </p>

        {/* Section 4 */}
        <h2 id="s4" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          4. Conservazione dei dati
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          I dati tecnici di navigazione registrati nei log di server sono conservati per un periodo massimo di 12 mesi, strettamente necessario alle finalità di sicurezza informatica descritte all'Articolo 2. Decorso tale periodo, i dati vengono eliminati automaticamente.
        </p>

        {/* Section 5 */}
        <h2 id="s5" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          5. Comunicazione dei dati a terzi
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          <strong>5.1</strong> I dati tecnici di navigazione possono essere resi accessibili, nei limiti strettamente necessari, alle seguenti categorie di soggetti:
        </p>
        <ul className="pl-5 mb-3.5 space-y-1.5">
          <li className="text-[#3d3528]">
            <strong>Km Zero Web Marketing Srls</strong>, in qualità di Responsabile del trattamento ex art. 28 GDPR, per la gestione tecnica e la manutenzione della Piattaforma;
          </li>
          <li className="text-[#3d3528]">
            <strong>Autorità pubbliche</strong>, ove la comunicazione sia obbligatoria per legge o disposta da provvedimento dell'autorità giudiziaria o amministrativa competente.
          </li>
        </ul>
        <p className="mb-3.5 text-[#3d3528]">
          <strong>5.2</strong> I dati non vengono venduti, ceduti o comunicati a soggetti terzi per finalità commerciali, pubblicitarie o di profilazione.
        </p>

        {/* Section Band */}
        <div className="bg-[#1a1510] text-[#f9f5ee] font-['Playfair_Display',serif] text-[0.7rem] tracking-[0.18em] uppercase px-3.5 py-1.5 inline-block mb-7 mt-13">
          Diritti e recapiti
        </div>

        {/* Section 6 */}
        <h2 id="s6" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          6. Diritti dell'interessato
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          <strong>6.1</strong> In qualità di interessato, l'Utente ha il diritto di esercitare, nei limiti e alle condizioni previsti dagli artt. 15-22 del GDPR, i seguenti diritti nei confronti del Titolare del trattamento:
        </p>
        <ul className="pl-5 mb-3.5 space-y-1.5">
          <li className="text-[#3d3528]">
            <strong>Accesso (art. 15):</strong> ottenere conferma che sia o meno in corso un trattamento di dati personali che lo riguardano e, in tal caso, accedervi;
          </li>
          <li className="text-[#3d3528]">
            <strong>Rettifica (art. 16):</strong> ottenere la correzione di dati inesatti o l'integrazione di dati incompleti;
          </li>
          <li className="text-[#3d3528]">
            <strong>Cancellazione (art. 17):</strong> ottenere la cancellazione dei propri dati personali, nei casi previsti dalla norma;
          </li>
          <li className="text-[#3d3528]">
            <strong>Limitazione del trattamento (art. 18):</strong> ottenere la limitazione del trattamento in determinate circostanze;
          </li>
          <li className="text-[#3d3528]">
            <strong>Opposizione (art. 21):</strong> opporsi al trattamento fondato sul legittimo interesse del Titolare del trattamento.
          </li>
        </ul>

        <div className="bg-[#f2ece0] border-l-3 border-[#7a6f5e] p-4 my-5 rounded-r text-[13px]">
          <p className="mb-0 text-[#7a6f5e] italic">
            In considerazione della natura anonima dei dati raccolti tramite le pagine del menù digitale, alcuni dei diritti sopra elencati — in particolare il diritto di accesso, rettifica e cancellazione di specifici dati riferiti alla propria persona — potrebbero essere di fatto inapplicabili, non essendo il Titolare del trattamento in grado di associare i dati tecnici di navigazione a un individuo identificato o identificabile.
          </p>
        </div>

        <p className="mb-3.5 text-[#3d3528]">
          <strong>6.2</strong> Per esercitare i propri diritti, l'Utente può contattare il Titolare del trattamento ai recapiti indicati all'Articolo 1. Il Titolare del trattamento si impegna a rispondere entro 30 giorni dal ricevimento della richiesta, estendibili fino a 3 mesi in casi di particolare complessità, con comunicazione motivata all'Utente.
        </p>

        {/* Section 7 */}
        <h2 id="s7" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          7. Diritto di reclamo al Garante
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          L'Utente che ritenga che il trattamento dei propri dati personali avvenga in violazione del GDPR ha il diritto di proporre reclamo al <strong>Garante per la Protezione dei Dati Personali</strong>:
        </p>

        <div className="bg-[#fdfbf7] border-l-3 border-[#b5410c] p-4 my-5 rounded-r">
          <p className="text-[13.5px] mb-0 text-[#3d3528]">
            Garante per la Protezione dei Dati Personali<br />
            Piazza Venezia 11 – 00187 Roma<br />
            E-mail: <a href="mailto:garante@gpdp.it" className="text-[#b5410c]">garante@gpdp.it</a> · <a href="mailto:urp@gpdp.it" className="text-[#b5410c]">urp@gpdp.it</a><br />
            Sito web: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#b5410c]">www.garanteprivacy.it</a>
          </p>
        </div>

        {/* Section 8 */}
        <h2 id="s8" className="font-['Playfair_Display',Georgia,serif] text-xl font-bold text-[#1a1510] mb-3.5 mt-10 pb-2 border-b border-[#d6c9b0]">
          8. Modifiche alla presente informativa
        </h2>
        <p className="mb-3.5 text-[#3d3528]">
          La presente informativa potrà essere modificata o aggiornata in qualsiasi momento, in particolare a seguito di variazioni normative o di modifiche alle modalità di trattamento dei dati. La versione aggiornata sarà sempre disponibile sulla Piattaforma con indicazione della data di ultima revisione. Si invita l'Utente a consultarla periodicamente.
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-[#1a1510] text-[#7a6f5e] py-8 px-6 text-center text-[12px] leading-[1.7]">
        <p className="mb-0">
          <strong className="text-[#f9f5ee]">Km Zero Web Marketing Srls</strong>
        </p>
        <p className="mt-1.5 mb-0">Via Giuseppe Fontecedro 2 – 01100 Viterbo (VT) · P.IVA 02312590561</p>
        <p className="mt-3 mb-0">
          <a href="#" className="text-[#e8c4aa] no-underline hover:underline">Condizioni Generali</a>
          <span className="mx-2">·</span>
          <a href="#" className="text-[#e8c4aa] no-underline hover:underline">Informativa Privacy</a>
        </p>
        <p className="mt-4 mb-0 text-[11px] text-[#4a4238]">
          Informativa redatta ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR)
        </p>
      </footer>
    </div>
  );
};

export default Privacy;